import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";



const PROJECT_TYPE_OPTIONS = [
    { value: "", label: "Selecciona tipo" },
    { value: "estrategico", label: "Estratégico" },
    { value: "operativo", label: "Operativo" },
    { value: "tecnologico", label: "Tecnológico" },
    { value: "comercial", label: "Comercial" },
    { value: "institucional", label: "Institucional" },
    { value: "transformacion_digital", label: "Transformación digital" },
    { value: "infraestructura", label: "Infraestructura" },
    { value: "innovacion", label: "Innovación" },
    { value: "capacitacion", label: "Capacitación" },
    { value: "cumplimiento", label: "Cumplimiento" },
    { value: "mejora_interna", label: "Mejora interna" },
    { value: "otro", label: "Otro" },
];

const emptyForm = {
    account_id: "",
    opportunity_id: "",
    name: "",
    code: "",
    description: "",
    project_type: "",
    priority: "media",
    stage: "inicio",
    status: "activo",
    health_status: "verde",
    progress_percent: 0,
    planned_start_date: "",
    planned_end_date: "",
    actual_start_date: "",
    actual_end_date: "",
    start_date: "",
    end_date: "",
    contract_value: "",
    budget_planned: "",
    budget_actual: "",
    currency: "MXN",
    project_manager: "",
    sponsor: "",
    strategic_impact: "",
    esg_focus: "",
    ebitda_impact_est: "",
    success_criteria: "",
    risk_level: "medio",
    notes: "",
};

function ActionButton({
    children,
    onClick,
    type = "button",
    bg = "#111827",
    color = "#fff",
    border = "1px solid #111827",
    disabled = false,
}) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            style={{
                padding: "10px 14px",
                borderRadius: 10,
                border,
                background: disabled ? "#9ca3af" : bg,
                color,
                fontWeight: 600,
                cursor: disabled ? "not-allowed" : "pointer",
                marginRight: 8,
                marginBottom: 8,
            }}
        >
            {children}
        </button>
    );
}

function SectionCard({ title, subtitle, children }) {
    return (
        <div
            style={{
                background: "#fff",
                borderRadius: 14,
                padding: 20,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                border: "1px solid #e5e7eb",
                marginBottom: 18,
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <h3 style={{ marginTop: 0, marginBottom: 6, fontSize: 18, color: "#111827" }}>
                    {title}
                </h3>
                {subtitle ? (
                    <div style={{ fontSize: 13, color: "#6b7280" }}>{subtitle}</div>
                ) : null}
            </div>
            {children}
        </div>
    );
}

function Input({ label, value, onChange, ...props }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
            <input
                value={value ?? ""}
                onChange={onChange}
                {...props}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: 14,
                    boxSizing: "border-box",
                    background: "#fff",
                }}
            />
        </div>
    );
}

function Select({ label, value, onChange, children }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
            <select
                value={value ?? ""}
                onChange={onChange}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: 14,
                    boxSizing: "border-box",
                    background: "#fff",
                }}
            >
                {children}
            </select>
        </div>
    );
}

function Textarea({ label, value, onChange, rows = 4 }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
            <textarea
                rows={rows}
                value={value ?? ""}
                onChange={onChange}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: 14,
                    boxSizing: "border-box",
                    resize: "vertical",
                    background: "#fff",
                }}
            />
        </div>
    );
}

function StepPill({ step, active, done, label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 12,
                border: active ? "1px solid #111827" : "1px solid #d1d5db",
                background: active ? "#111827" : done ? "#f8fafc" : "#fff",
                color: active ? "#fff" : "#111827",
                cursor: "pointer",
                fontWeight: 600,
            }}
        >
            <span
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: active ? "#fff" : done ? "#dcfce7" : "#f3f4f6",
                    color: active ? "#111827" : "#374151",
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                {done ? "✓" : step}
            </span>
            <span>{label}</span>
        </button>
    );
}

function ReviewRow({ label, value }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, color: "#111827", fontWeight: 600 }}>{value || "—"}</div>
        </div>
    );
}

function formatMoneyPreview(value, currency = "MXN") {
    if (value === "" || value === null || value === undefined) return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    try {
        return new Intl.NumberFormat("es-MX", {
            style: "currency",
            currency: currency || "MXN",
            maximumFractionDigits: 0,
        }).format(num);
    } catch {
        return `${currency || "MXN"} ${num.toLocaleString("es-MX")}`;
    }
}

export default function ProjectFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditMode = useMemo(() => Boolean(id), [id]);

    const [form, setForm] = useState(() => ({
        ...emptyForm,
        stage: "inicio",
        status: "activo",
        health_status: "verde",
        planned_start_date: new Date().toISOString().slice(0, 10),
    }));
    const [accounts, setAccounts] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [activeStep, setActiveStep] = useState(1);

    const fetchJsonSafe = async (url) => {
        const res = await fetch(url);
        const raw = await res.text();

        let data = null;
        try {
            data = raw ? JSON.parse(raw) : null;
        } catch {
            throw new Error(`Respuesta inválida en ${url}: ${raw || "[vacío]"}`);
        }

        return data;
    };

    const normalizeList = (payload, key) => {
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload[key])) return payload[key];
        return [];
    };

    const loadCatalogs = async () => {
        const [accountsPayload, opportunitiesPayload] = await Promise.all([
            fetchJsonSafe(`${API_BASE}/accounts_list.php`),
            fetchJsonSafe(`${API_BASE}/opportunities_list.php`),
        ]);

        setAccounts(normalizeList(accountsPayload, "accounts"));
        setOpportunities(normalizeList(opportunitiesPayload, "opportunities"));
    };

    const loadProject = async () => {
        if (!isEditMode) return;

        const payload = await fetchJsonSafe(`${API_BASE}/project_detail.php?id=${id}`);
        if (!payload?.success) {
            throw new Error(payload?.message || "No se pudo cargar el proyecto");
        }

        const p = payload.project || {};

        setForm({
            account_id: p.account_id ?? "",
            opportunity_id: p.opportunity_id ?? "",
            name: p.name ?? "",
            code: p.code ?? "",
            description: p.description ?? "",
            project_type: p.project_type ?? "",
            priority: p.priority ?? "media",
            stage: p.stage ?? "inicio",
            status: p.status ?? "activo",
            health_status: p.health_status ?? "verde",
            progress_percent: p.progress_percent ?? 0,
            planned_start_date: p.planned_start_date ?? "",
            planned_end_date: p.planned_end_date ?? "",
            actual_start_date: p.actual_start_date ?? "",
            actual_end_date: p.actual_end_date ?? "",
            start_date: p.start_date ?? "",
            end_date: p.end_date ?? "",
            contract_value: p.contract_value ?? "",
            budget_planned: p.budget_planned ?? "",
            budget_actual: p.budget_actual ?? "",
            currency: p.currency ?? "MXN",
            project_manager: p.project_manager ?? "",
            sponsor: p.sponsor ?? "",
            strategic_impact: p.strategic_impact ?? "",
            esg_focus: p.esg_focus ?? "",
            ebitda_impact_est: p.ebitda_impact_est ?? "",
            success_criteria: p.success_criteria ?? "",
            risk_level: p.risk_level ?? "medio",
            notes: p.notes ?? "",
        });
    };

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                setError("");
                await Promise.all([loadCatalogs(), loadProject()]);
            } catch (err) {
                setError(err.message || "Error al cargar la pantalla");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [id]);

    useEffect(() => {
        if (isEditMode) {
            setActiveStep(1);
        }
    }, [isEditMode]);

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const validateStep = (step) => {
        if (step === 1) {
            if (!String(form.name || "").trim()) {
                return "El nombre del proyecto es obligatorio";
            }
            if (!String(form.project_type || "").trim()) {
                return "Selecciona el tipo de proyecto";
            }
        }

        if (step === 2) {
            if (!String(form.planned_start_date || "").trim()) {
                return "El inicio planeado es obligatorio";
            }
            if (!String(form.priority || "").trim()) {
                return "La prioridad es obligatoria";
            }
        }

        return "";
    };

    const goNextStep = () => {
        const msg = validateStep(activeStep);
        if (msg) {
            setError(msg);
            return;
        }
        setError("");
        setActiveStep((prev) => Math.min(3, prev + 1));
    };

    const goPrevStep = () => {
        setError("");
        setActiveStep((prev) => Math.max(1, prev - 1));
    };

    const submitJson = async (url, payload) => {
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const raw = await res.text();

        let data = null;
        try {
            data = raw ? JSON.parse(raw) : null;
        } catch {
            throw new Error(`Respuesta inválida en ${url}: ${raw || "[vacío]"}`);
        }

        if (!data?.success) {
            throw new Error(data?.message || "No se pudo guardar el proyecto");
        }

        return data;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError("");

            const finalValidation = validateStep(1) || validateStep(2);
            if (finalValidation) {
                throw new Error(finalValidation);
            }

            const payload = {
                ...form,
                account_id: String(form.account_id || "").trim() !== "" ? Number(form.account_id) : null,
                opportunity_id: String(form.opportunity_id || "").trim() !== "" ? Number(form.opportunity_id) : null,
                progress_percent:
                    String(form.progress_percent || "").trim() !== "" ? Number(form.progress_percent) : 0,
                contract_value:
                    String(form.contract_value || "").trim() !== "" ? Number(form.contract_value) : null,
                budget_planned:
                    String(form.budget_planned || "").trim() !== "" ? Number(form.budget_planned) : null,
                budget_actual:
                    String(form.budget_actual || "").trim() !== "" ? Number(form.budget_actual) : null,
                ebitda_impact_est:
                    String(form.ebitda_impact_est || "").trim() !== "" ? Number(form.ebitda_impact_est) : null,
            };

            const endpoint = isEditMode
                ? `${API_BASE}/project_update.php`
                : `${API_BASE}/projects.php`;

            const body = isEditMode ? { id: Number(id), ...payload } : payload;

            const data = await submitJson(endpoint, body);

            const projectId = data.project_id || data.id || id;
            navigate(`/projects/${projectId}`);
        } catch (err) {
            setError(err.message || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    const selectedAccountName =
        accounts.find((a) => String(a.id) === String(form.account_id))?.name ||
        accounts.find((a) => String(a.id) === String(form.account_id))?.account_name ||
        "";

    const selectedOpportunityName =
        opportunities.find((o) => String(o.id) === String(form.opportunity_id))?.name ||
        opportunities.find((o) => String(o.id) === String(form.opportunity_id))?.opportunity_name ||
        "";

    const selectedProjectTypeLabel =
        PROJECT_TYPE_OPTIONS.find((item) => item.value === form.project_type)?.label || "";

    if (loading) {
        return (
            <MainLayout>
                <div style={{ padding: 24 }}>Cargando formulario de proyecto...</div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh" }}>
                <div style={{ marginBottom: 18 }}>
                    <ActionButton
                        onClick={() => navigate(isEditMode ? `/projects/${id}` : "/projects")}
                        bg="#fff"
                        color="#111827"
                        border="1px solid #d1d5db"
                    >
                        ← Volver
                    </ActionButton>
                </div>

                <div style={{ marginBottom: 20 }}>
                    <h1 style={{ marginTop: 0, marginBottom: 6, fontSize: 28, color: "#111827" }}>
                        {isEditMode ? "Editar proyecto PMO" : "Crear nuevo proyecto"}
                    </h1>

                    <div style={{ color: "#6b7280", fontSize: 14 }}>
                        {isEditMode
                            ? "Actualiza la información base del proyecto."
                            : "Define la base del proyecto en 3 pasos. El CRM es opcional: ORBIS puede crear proyectos independientes o vinculados a cuentas/oportunidades."}
                    </div>
                </div>

                {!isEditMode ? (
                    <SectionCard title="Nuevo Proyecto PRO" subtitle="Elige cómo iniciar. En C4.1 dejamos la base lista para plantillas e IA sin obligar CRM.">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                            <button
                                type="button"
                                onClick={() => setActiveStep(1)}
                                style={{
                                    textAlign: "left",
                                    padding: 18,
                                    borderRadius: 18,
                                    border: "1px solid rgba(0,178,255,.26)",
                                    background: "linear-gradient(180deg, rgba(8,47,73,.94), rgba(2,25,48,.98))",
                                    color: "#fff",
                                    cursor: "pointer",
                                }}
                            >
                                <div style={{ color: "#f4b91b", fontWeight: 900, fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>En blanco</div>
                                <div style={{ fontWeight: 950, fontSize: 18, marginTop: 6 }}>Captura manual guiada</div>
                                <div style={{ color: "#b9e8ff", fontSize: 13, marginTop: 6, lineHeight: 1.45 }}>Crea un proyecto independiente o vinculado al CRM.</div>
                            </button>

                            <button
                                type="button"
                                onClick={() => navigate("/project-templates")}
                                style={{
                                    textAlign: "left",
                                    padding: 18,
                                    borderRadius: 18,
                                    border: "1px solid rgba(244,185,27,.42)",
                                    background: "linear-gradient(180deg, rgba(113,63,18,.34), rgba(2,25,48,.98))",
                                    color: "#fff",
                                    cursor: "pointer",
                                }}
                            >
                                <div style={{ color: "#f4b91b", fontWeight: 900, fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>Desde plantilla</div>
                                <div style={{ fontWeight: 950, fontSize: 18, marginTop: 6 }}>Arranque con estructura PMO</div>
                                <div style={{ color: "#fde68a", fontSize: 13, marginTop: 6, lineHeight: 1.45 }}>Usa proyectos modelo para crear WBS, riesgos, entregables e hitos.</div>
                            </button>

                            <button
                                type="button"
                                onClick={() => setError("C4.2 preparará la conexión OpenAI. En C4.1 dejamos lista la experiencia de arranque.")}
                                style={{
                                    textAlign: "left",
                                    padding: 18,
                                    borderRadius: 18,
                                    border: "1px solid rgba(139,92,246,.42)",
                                    background: "linear-gradient(180deg, rgba(88,28,135,.34), rgba(2,25,48,.98))",
                                    color: "#fff",
                                    cursor: "pointer",
                                }}
                            >
                                <div style={{ color: "#c4b5fd", fontWeight: 900, fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase" }}>Con IA</div>
                                <div style={{ fontWeight: 950, fontSize: 18, marginTop: 6 }}>Describe tu proyecto</div>
                                <div style={{ color: "#e9d5ff", fontSize: 13, marginTop: 6, lineHeight: 1.45 }}>ORBIS generará propuesta de estructura en C4.2.</div>
                            </button>
                        </div>
                    </SectionCard>
                ) : null}

                {!isEditMode ? (
                    <SectionCard title="Flujo de alta" subtitle="Captura guiada del proyecto">
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <StepPill step={1} label="Identificación" active={activeStep === 1} done={activeStep > 1} onClick={() => setActiveStep(1)} />
                            <StepPill step={2} label="Planeación" active={activeStep === 2} done={activeStep > 2} onClick={() => setActiveStep(2)} />
                            <StepPill step={3} label="Revisión final" active={activeStep === 3} done={false} onClick={() => setActiveStep(3)} />
                        </div>
                    </SectionCard>
                ) : null}

                {error ? (
                    <div
                        style={{
                            background: "#fee2e2",
                            color: "#991b1b",
                            padding: 14,
                            borderRadius: 10,
                            marginBottom: 16,
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                <form onSubmit={handleSubmit}>
                    {(isEditMode || activeStep === 1) && (
                        <>
                            <SectionCard
                                title="Identificación del proyecto"
                                subtitle="Datos base y clasificación principal"
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
                                        gap: 14,
                                    }}
                                >
                                    <Select
                                        label="Cuenta (opcional)"
                                        value={form.account_id}
                                        onChange={(e) => handleChange("account_id", e.target.value)}
                                    >
                                        <option value="">Sin cuenta / proyecto independiente</option>
                                        {accounts.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.name || a.account_name || `Cuenta ${a.id}`}
                                            </option>
                                        ))}
                                    </Select>

                                    <Select
                                        label="Oportunidad"
                                        value={form.opportunity_id}
                                        onChange={(e) => handleChange("opportunity_id", e.target.value)}
                                    >
                                        <option value="">Sin oportunidad</option>
                                        {opportunities.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {o.name || o.opportunity_name || `Oportunidad ${o.id}`}
                                            </option>
                                        ))}
                                    </Select>

                                    <Input
                                        label="Nombre del proyecto"
                                        value={form.name}
                                        onChange={(e) => handleChange("name", e.target.value)}
                                    />

                                    <Input
                                        label="Código"
                                        value={form.code}
                                        onChange={(e) => handleChange("code", e.target.value)}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(6, minmax(150px, 1fr))",
                                        gap: 14,
                                    }}
                                >
                                    <Select
                                        label="Tipo de proyecto"
                                        value={form.project_type}
                                        onChange={(e) => handleChange("project_type", e.target.value)}
                                    >
                                        {PROJECT_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>

                                    <Select
                                        label="Prioridad"
                                        value={form.priority}
                                        onChange={(e) => handleChange("priority", e.target.value)}
                                    >
                                        <option value="baja">baja</option>
                                        <option value="media">media</option>
                                        <option value="alta">alta</option>
                                        <option value="critica">critica</option>
                                    </Select>

                                    <Select
                                        label="Etapa"
                                        value={form.stage}
                                        onChange={(e) => handleChange("stage", e.target.value)}
                                    >
                                        <option value="inicio">inicio</option>
                                        <option value="planeacion">planeacion</option>
                                        <option value="ejecucion">ejecucion</option>
                                        <option value="monitoreo">monitoreo</option>
                                        <option value="cierre">cierre</option>
                                        <option value="cancelado">cancelado</option>
                                    </Select>

                                    <Select
                                        label="Estatus"
                                        value={form.status}
                                        onChange={(e) => handleChange("status", e.target.value)}
                                    >
                                        <option value="activo">activo</option>
                                        <option value="pausado">pausado</option>
                                        <option value="terminado">terminado</option>
                                        <option value="cancelado">cancelado</option>
                                    </Select>

                                    <Select
                                        label="Salud"
                                        value={form.health_status}
                                        onChange={(e) => handleChange("health_status", e.target.value)}
                                    >
                                        <option value="verde">verde</option>
                                        <option value="amarillo">amarillo</option>
                                        <option value="rojo">rojo</option>
                                    </Select>

                                    <Select
                                        label="Nivel de riesgo"
                                        value={form.risk_level}
                                        onChange={(e) => handleChange("risk_level", e.target.value)}
                                    >
                                        <option value="bajo">bajo</option>
                                        <option value="medio">medio</option>
                                        <option value="alto">alto</option>
                                    </Select>
                                </div>

                                <Textarea
                                    label="Descripción"
                                    value={form.description}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    rows={5}
                                />
                            </SectionCard>
                        </>
                    )}

                    {(isEditMode || activeStep === 2) && (
                        <>
                            <SectionCard
                                title="Planeación, fechas y presupuesto"
                                subtitle="Captura operativa y financiera base"
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(6, minmax(150px, 1fr))",
                                        gap: 14,
                                    }}
                                >
                                    <Input
                                        label="Inicio planeado"
                                        type="date"
                                        value={form.planned_start_date}
                                        onChange={(e) => handleChange("planned_start_date", e.target.value)}
                                    />
                                    <Input
                                        label="Fin planeado"
                                        type="date"
                                        value={form.planned_end_date}
                                        onChange={(e) => handleChange("planned_end_date", e.target.value)}
                                    />
                                    <Input
                                        label="Inicio real"
                                        type="date"
                                        value={form.actual_start_date}
                                        onChange={(e) => handleChange("actual_start_date", e.target.value)}
                                    />
                                    <Input
                                        label="Fin real"
                                        type="date"
                                        value={form.actual_end_date}
                                        onChange={(e) => handleChange("actual_end_date", e.target.value)}
                                    />
                                    <Input
                                        label="Inicio operativo"
                                        type="date"
                                        value={form.start_date}
                                        onChange={(e) => handleChange("start_date", e.target.value)}
                                    />
                                    <Input
                                        label="Fin operativo"
                                        type="date"
                                        value={form.end_date}
                                        onChange={(e) => handleChange("end_date", e.target.value)}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "220px 180px 1fr 1fr",
                                        gap: 14,
                                        marginTop: 10,
                                    }}
                                >
                                    <Input
                                        label="% avance del proyecto"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={form.progress_percent}
                                        onChange={(e) => handleChange("progress_percent", e.target.value)}
                                    />
                                    <Input
                                        label="Moneda"
                                        value={form.currency}
                                        onChange={(e) => handleChange("currency", e.target.value)}
                                    />
                                    <Input
                                        label="Project Manager"
                                        value={form.project_manager}
                                        onChange={(e) => handleChange("project_manager", e.target.value)}
                                    />
                                    <Input
                                        label="Sponsor"
                                        value={form.sponsor}
                                        onChange={(e) => handleChange("sponsor", e.target.value)}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(3, minmax(160px, 1fr))",
                                        gap: 14,
                                        marginTop: 10,
                                    }}
                                >
                                    <Input
                                        label="Valor contrato"
                                        type="number"
                                        step="0.01"
                                        value={form.contract_value}
                                        onChange={(e) => handleChange("contract_value", e.target.value)}
                                    />
                                    <Input
                                        label="Presupuesto planeado"
                                        type="number"
                                        step="0.01"
                                        value={form.budget_planned}
                                        onChange={(e) => handleChange("budget_planned", e.target.value)}
                                    />
                                    <Input
                                        label="Presupuesto actual"
                                        type="number"
                                        step="0.01"
                                        value={form.budget_actual}
                                        onChange={(e) => handleChange("budget_actual", e.target.value)}
                                    />
                                </div>
                            </SectionCard>

                            <SectionCard
                                title="Impacto y criterios"
                                subtitle="Contexto ejecutivo y notas"
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 14,
                                    }}
                                >
                                    <Textarea
                                        label="Impacto estratégico"
                                        value={form.strategic_impact}
                                        onChange={(e) => handleChange("strategic_impact", e.target.value)}
                                        rows={4}
                                    />
                                    <Textarea
                                        label="Criterios de éxito"
                                        value={form.success_criteria}
                                        onChange={(e) => handleChange("success_criteria", e.target.value)}
                                        rows={4}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 220px",
                                        gap: 14,
                                        marginTop: 10,
                                    }}
                                >
                                    <Input
                                        label="Enfoque ESG"
                                        value={form.esg_focus}
                                        onChange={(e) => handleChange("esg_focus", e.target.value)}
                                    />
                                    <Input
                                        label="EBITDA estimado"
                                        type="number"
                                        step="0.01"
                                        value={form.ebitda_impact_est}
                                        onChange={(e) => handleChange("ebitda_impact_est", e.target.value)}
                                    />
                                </div>

                                <Textarea
                                    label="Notas"
                                    value={form.notes}
                                    onChange={(e) => handleChange("notes", e.target.value)}
                                    rows={4}
                                />
                            </SectionCard>
                        </>
                    )}

                    {!isEditMode && activeStep === 3 && (
                        <SectionCard
                            title="Revisión final"
                            subtitle="Confirma la base del proyecto antes de crearlo"
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
                                    gap: 20,
                                }}
                            >
                                <div>
                                    <ReviewRow label="Cuenta" value={selectedAccountName} />
                                    <ReviewRow label="Oportunidad" value={selectedOpportunityName || "Sin oportunidad"} />
                                    <ReviewRow label="Nombre" value={form.name} />
                                    <ReviewRow label="Código" value={form.code} />
                                    <ReviewRow label="Tipo de proyecto" value={selectedProjectTypeLabel} />
                                </div>
                                <div>
                                    <ReviewRow label="Prioridad" value={form.priority} />
                                    <ReviewRow label="Etapa" value={form.stage} />
                                    <ReviewRow label="Estatus" value={form.status} />
                                    <ReviewRow label="Salud" value={form.health_status} />
                                    <ReviewRow label="Riesgo" value={form.risk_level} />
                                </div>
                                <div>
                                    <ReviewRow label="Inicio planeado" value={form.planned_start_date} />
                                    <ReviewRow label="Fin planeado" value={form.planned_end_date} />
                                    <ReviewRow label="Presupuesto planeado" value={formatMoneyPreview(form.budget_planned, form.currency)} />
                                    <ReviewRow label="Presupuesto actual" value={formatMoneyPreview(form.budget_actual, form.currency)} />
                                    <ReviewRow label="Valor contrato" value={formatMoneyPreview(form.contract_value, form.currency)} />
                                </div>
                            </div>

                            <div
                                style={{
                                    marginTop: 12,
                                    padding: 14,
                                    borderRadius: 12,
                                    background: "#f8fafc",
                                    border: "1px solid #e5e7eb",
                                }}
                            >
                                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Descripción</div>
                                <div style={{ fontSize: 14, color: "#111827" }}>{form.description || "—"}</div>
                            </div>
                        </SectionCard>
                    )}

                    <div style={{ marginTop: 8 }}>
                        {!isEditMode ? (
                            <>
                                {activeStep > 1 ? (
                                    <ActionButton
                                        type="button"
                                        onClick={goPrevStep}
                                        bg="#fff"
                                        color="#111827"
                                        border="1px solid #d1d5db"
                                    >
                                        Anterior
                                    </ActionButton>
                                ) : null}

                                {activeStep < 3 ? (
                                    <ActionButton type="button" onClick={goNextStep}>
                                        Siguiente
                                    </ActionButton>
                                ) : (
                                    <ActionButton type="submit" disabled={saving}>
                                        {saving ? "Guardando..." : "Crear proyecto"}
                                    </ActionButton>
                                )}
                            </>
                        ) : (
                            <ActionButton type="submit" disabled={saving}>
                                {saving ? "Guardando..." : "Actualizar proyecto"}
                            </ActionButton>
                        )}

                        <ActionButton
                            type="button"
                            onClick={() => navigate(isEditMode ? `/projects/${id}` : "/projects")}
                            bg="#fff"
                            color="#111827"
                            border="1px solid #d1d5db"
                        >
                            Cancelar
                        </ActionButton>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
}
